import { useState } from "react";
import { Quest, Artist, communityFeed } from "@/mocks/artistData";

interface CollabVideo {
  id: string;
  user: { name: string; avatar: string; badge: string };
  thumbnail: string;
  title: string;
  duration: string;
  likes: number;
  timeAgo: string;
  lastFrameUrl: string;
  generatedImageUrl: string;
}

interface CollabWorkModalProps {
  quest: Quest;
  artist: Artist;
  accentColor: string;
  onClose: () => void;
  onSelectAsset: (video: CollabVideo, tokenRewarded: number) => void;
}

const TOKEN_REWARD = 50;

// 마지막 장면 이미지 (썸네일 기반 변형)
const lastFrameImages: Record<string, string> = {
  "feed-001": "https://public.readdy.ai/ai/img_res/7f03c6b8cd4e734d95c67160be7cd512.jpg",
  "feed-002": "https://readdy.ai/api/search-image?query=kpop%20male%20idol%20dance%20final%20pose%2C%20dramatic%20dark%20stage%2C%20neon%20blue%20light%2C%20powerful%20stance%2C%20cinematic%20still%20frame%2C%20concert%20atmosphere&width=600&height=340&seq=collab-last-002&orientation=landscape",
  "feed-003": "https://readdy.ai/api/search-image?query=korean%20drama%20emotional%20final%20scene%2C%20actor%20close%20up%2C%20cinematic%20indoor%20lighting%2C%20dramatic%20expression%2C%20still%20frame%2C%20professional%20film%20atmosphere&width=600&height=340&seq=collab-last-003&orientation=landscape",
  "feed-004": "https://readdy.ai/api/search-image?query=korean%20movie%20poster%20final%20frame%2C%20hero%20silhouette%2C%20dark%20atmospheric%20background%2C%20dramatic%20lighting%2C%20cinematic%20still&width=600&height=340&seq=collab-last-004&orientation=landscape",
  "feed-005": "https://readdy.ai/api/search-image?query=webtoon%20character%20final%20scene%2C%20colorful%20costume%2C%20dynamic%20final%20pose%2C%20vibrant%20background%2C%20cinematic%20still%20frame&width=600&height=340&seq=collab-last-005&orientation=landscape",
  "feed-006": "https://readdy.ai/api/search-image?query=kpop%20female%20idol%20graceful%20final%20dance%20pose%2C%20purple%20lavender%20lighting%2C%20elegant%20stance%2C%20flowing%20dress%2C%20cinematic%20still%20frame&width=600&height=340&seq=collab-last-006&orientation=landscape",
};

const generatedImages: Record<string, string> = {
  "feed-001": "https://readdy.ai/api/search-image?query=kpop%20female%20idol%20AI%20generated%20image%2C%20cherry%20blossom%20theme%2C%20pink%20aesthetic%2C%20digital%20art%20style%2C%20clean%20simple%20background%2C%20high%20quality%20illustration&width=400&height=400&seq=collab-gen-001&orientation=squarish",
  "feed-002": "https://readdy.ai/api/search-image?query=kpop%20male%20idol%20AI%20generated%20image%2C%20blue%20neon%20theme%2C%20dynamic%20pose%2C%20digital%20art%20style%2C%20clean%20background%2C%20high%20quality%20illustration&width=400&height=400&seq=collab-gen-002&orientation=squarish",
  "feed-003": "https://readdy.ai/api/search-image?query=korean%20drama%20character%20AI%20generated%20image%2C%20cinematic%20style%2C%20emotional%20expression%2C%20digital%20art%2C%20clean%20background&width=400&height=400&seq=collab-gen-003&orientation=squarish",
  "feed-004": "https://readdy.ai/api/search-image?query=korean%20film%20character%20AI%20generated%20image%2C%20dramatic%20style%2C%20hero%20pose%2C%20digital%20art%2C%20dark%20background&width=400&height=400&seq=collab-gen-004&orientation=squarish",
  "feed-005": "https://readdy.ai/api/search-image?query=webtoon%20character%20AI%20generated%20image%2C%20colorful%20vibrant%20style%2C%20dynamic%20pose%2C%20digital%20illustration%2C%20clean%20background&width=400&height=400&seq=collab-gen-005&orientation=squarish",
  "feed-006": "https://readdy.ai/api/search-image?query=kpop%20female%20idol%20AI%20generated%20image%2C%20purple%20lavender%20theme%2C%20graceful%20pose%2C%20digital%20art%20style%2C%20clean%20background&width=400&height=400&seq=collab-gen-006&orientation=squarish",
};

export default function CollabWorkModal({
  quest, artist, accentColor, onClose, onSelectAsset,
}: CollabWorkModalProps) {
  const [selectedVideo, setSelectedVideo] = useState<CollabVideo | null>(null);
  const [previewTab, setPreviewTab] = useState<"lastFrame" | "generated">("lastFrame");
  const [confirmed, setConfirmed] = useState(false);
  const [tokenAnimating, setTokenAnimating] = useState(false);

  // 같은 아티스트의 커뮤니티 피드 영상 (다른 창작자들)
  const collabVideos: CollabVideo[] = communityFeed
    .filter((f) => f.artistId === quest.artistId)
    .map((f) => ({
      id: f.id,
      user: f.user,
      thumbnail: f.thumbnail,
      title: f.title,
      duration: f.duration,
      likes: f.likes,
      timeAgo: f.timeAgo,
      lastFrameUrl: lastFrameImages[f.id] ?? f.thumbnail,
      generatedImageUrl: generatedImages[f.id] ?? f.thumbnail,
    }));

  // 같은 아티스트 영상이 없으면 전체에서 랜덤 3개
  const displayVideos = collabVideos.length > 0
    ? collabVideos
    : communityFeed.slice(0, 3).map((f) => ({
        id: f.id,
        user: f.user,
        thumbnail: f.thumbnail,
        title: f.title,
        duration: f.duration,
        likes: f.likes,
        timeAgo: f.timeAgo,
        lastFrameUrl: lastFrameImages[f.id] ?? f.thumbnail,
        generatedImageUrl: generatedImages[f.id] ?? f.thumbnail,
      }));

  const handleConfirm = () => {
    if (!selectedVideo) return;
    setTokenAnimating(true);
    setTimeout(() => {
      setTokenAnimating(false);
      setConfirmed(true);
    }, 1200);
    setTimeout(() => {
      onSelectAsset(selectedVideo, TOKEN_REWARD);
    }, 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-2xl border flex flex-col"
        style={{ background: "#0f0f18", borderColor: `${accentColor}30` }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 border-b flex-shrink-0"
          style={{ borderColor: "rgba(255,255,255,0.08)", background: `${accentColor}08` }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0"
              style={{ background: `${accentColor}20` }}
            >
              <i className="ri-links-line text-sm" style={{ color: accentColor }} />
            </div>
            <div>
              <h3 className="text-white font-bold text-sm">연결하여 작업</h3>
              <p className="text-white/35 text-[11px] mt-px">
                다른 창작자의 영상 마지막 장면을 이어서 창작하세요
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-lg border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 transition-all cursor-pointer"
          >
            <i className="ri-close-line text-sm" />
          </button>
        </div>

        {/* Token reward notice */}
        <div
          className="mx-5 mt-4 flex-shrink-0 flex items-center gap-3 px-4 py-3 rounded-xl border"
          style={{ background: `${accentColor}08`, borderColor: `${accentColor}25` }}
        >
          <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: `${accentColor}20` }}>
            <i className="ri-coin-fill text-base" style={{ color: accentColor }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white/70 text-xs leading-relaxed">
              연결 작업 시 원본 창작자에게{" "}
              <span className="font-bold" style={{ color: accentColor }}>
                {TOKEN_REWARD} 토큰
              </span>
              이 자동 지급됩니다.
            </p>
            <p className="text-white/30 text-[11px] mt-0.5">
              창작 생태계 기여에 대한 보상 · 즉시 지급
            </p>
          </div>
          <div
            className="flex-shrink-0 px-2.5 py-1 rounded-lg text-[11px] font-bold"
            style={{ background: `${accentColor}20`, color: accentColor }}
          >
            자동 지급
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3" style={{ scrollbarWidth: "none" }}>

          {!selectedVideo ? (
            <>
              <p className="text-white/40 text-xs mb-3">
                <i className="ri-film-line mr-1.5" />
                {quest.title} 캠페인의 다른 창작자 영상 ({displayVideos.length}개)
              </p>

              {displayVideos.map((video) => (
                <button
                  key={video.id}
                  onClick={() => setSelectedVideo(video)}
                  className="w-full flex items-start gap-3 p-3 rounded-xl border text-left transition-all duration-150 cursor-pointer group"
                  style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)" }}
                >
                  {/* Thumbnail */}
                  <div className="relative w-28 flex-shrink-0 rounded-lg overflow-hidden border border-white/10" style={{ aspectRatio: "16/9" }}>
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-7 h-7 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                        <i className="ri-play-fill text-white text-xs" />
                      </div>
                    </div>
                    {video.duration !== "포스터" && (
                      <div className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded text-[9px] font-bold bg-black/70 text-white/80">
                        {video.duration}
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-1">
                      <div className="w-4 h-4 rounded-full overflow-hidden flex-shrink-0">
                        <img src={video.user.avatar} alt={video.user.name} className="w-full h-full object-cover" />
                      </div>
                      <span className="text-white/50 text-[11px] truncate">{video.user.name}</span>
                      <span
                        className="flex-shrink-0 px-1.5 py-px rounded text-[9px] font-bold"
                        style={{ background: `${accentColor}20`, color: accentColor }}
                      >
                        {video.user.badge}
                      </span>
                    </div>
                    <p className="text-white/80 text-xs font-semibold leading-snug mb-1.5 line-clamp-2">
                      {video.title}
                    </p>
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1 text-white/30 text-[11px]">
                        <i className="ri-heart-line text-xs" />
                        {video.likes.toLocaleString()}
                      </span>
                      <span className="text-white/20 text-[11px]">{video.timeAgo}</span>
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-arrow-right-s-line text-white/20 group-hover:text-white/50 transition-colors" />
                  </div>
                </button>
              ))}
            </>
          ) : (
            /* Asset Preview */
            <div className="space-y-4">
              {/* Back button */}
              <button
                onClick={() => { setSelectedVideo(null); setConfirmed(false); }}
                className="flex items-center gap-1.5 text-white/40 text-xs hover:text-white/70 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-sm" />
                목록으로
              </button>

              {/* Selected creator info */}
              <div
                className="flex items-center gap-3 p-3 rounded-xl border"
                style={{ borderColor: `${accentColor}25`, background: `${accentColor}06` }}
              >
                <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 border border-white/15">
                  <img src={selectedVideo.user.avatar} alt={selectedVideo.user.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white/80 text-xs font-semibold">{selectedVideo.user.name}</p>
                  <p className="text-white/35 text-[11px] truncate">{selectedVideo.title}</p>
                </div>
                <div
                  className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold flex-shrink-0"
                  style={{ background: `${accentColor}20`, color: accentColor }}
                >
                  <i className="ri-coin-fill text-xs" />
                  +{TOKEN_REWARD} 토큰 지급
                </div>
              </div>

              {/* Tab switcher */}
              <div
                className="flex rounded-xl p-1 gap-1"
                style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
              >
                {(["lastFrame", "generated"] as const).map((tab) => {
                  const meta = {
                    lastFrame: { label: "마지막 장면", icon: "ri-film-fill" },
                    generated: { label: "생성 이미지", icon: "ri-image-ai-line" },
                  }[tab];
                  return (
                    <button
                      key={tab}
                      onClick={() => setPreviewTab(tab)}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap"
                      style={
                        previewTab === tab
                          ? { background: accentColor, color: "#0a0a0f" }
                          : { color: "rgba(255,255,255,0.35)" }
                      }
                    >
                      <i className={`${meta.icon} text-xs`} />
                      {meta.label}
                    </button>
                  );
                })}
              </div>

              {/* Asset preview */}
              <div className="rounded-xl overflow-hidden border border-white/10">
                {previewTab === "lastFrame" ? (
                  <div className="relative" style={{ aspectRatio: "16/9" }}>
                    <img
                      src={selectedVideo.lastFrameUrl}
                      alt="마지막 장면"
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute inset-0 flex items-end p-3" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 60%)" }}>
                      <div>
                        <div
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-bold mb-1"
                          style={{ background: `${accentColor}25`, color: accentColor, border: `1px solid ${accentColor}40` }}
                        >
                          <i className="ri-film-fill text-xs" />
                          마지막 장면 · Asset으로 활용
                        </div>
                        <p className="text-white/60 text-[11px]">이 장면에서 이어지는 영상을 제작하세요</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="relative flex items-center justify-center bg-white/3" style={{ aspectRatio: "1/1", maxHeight: "280px" }}>
                    <img
                      src={selectedVideo.generatedImageUrl}
                      alt="생성 이미지"
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute inset-0 flex items-end p-3" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 60%)" }}>
                      <div>
                        <div
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-bold mb-1"
                          style={{ background: `${accentColor}25`, color: accentColor, border: `1px solid ${accentColor}40` }}
                        >
                          <i className="ri-image-ai-line text-xs" />
                          생성 이미지 · Asset으로 활용
                        </div>
                        <p className="text-white/60 text-[11px]">이 이미지를 참조 asset으로 사용하세요</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Asset usage guide */}
              <div className="grid grid-cols-2 gap-2">
                {[
                  { icon: "ri-scissors-cut-line", title: "이어서 창작", desc: "마지막 장면에서 연속되는 영상 제작" },
                  { icon: "ri-image-2-line", title: "스타일 참조", desc: "생성 이미지의 스타일을 프롬프트에 반영" },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-2 p-3 rounded-xl border border-white/8 bg-white/3"
                  >
                    <div
                      className="w-6 h-6 flex items-center justify-center rounded-md flex-shrink-0 mt-0.5"
                      style={{ background: `${accentColor}15` }}
                    >
                      <i className={`${item.icon} text-xs`} style={{ color: accentColor }} />
                    </div>
                    <div>
                      <p className="text-white/70 text-[11px] font-semibold">{item.title}</p>
                      <p className="text-white/30 text-[10px] mt-0.5 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer CTA */}
        {selectedVideo && (
          <div
            className="px-5 py-4 border-t flex-shrink-0"
            style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(0,0,0,0.3)" }}
          >
            {!confirmed ? (
              <button
                onClick={handleConfirm}
                disabled={tokenAnimating}
                className="w-full py-3.5 rounded-xl font-bold text-sm text-white transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2.5 disabled:opacity-70"
                style={{ background: `linear-gradient(135deg, ${accentColor}, ${accentColor}99)` }}
              >
                {tokenAnimating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    토큰 지급 중...
                  </>
                ) : (
                  <>
                    <i className="ri-links-line text-base" />
                    이 영상 asset으로 작업 시작
                    <span
                      className="flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold"
                      style={{ background: "rgba(0,0,0,0.25)", color: "rgba(255,255,255,0.8)" }}
                    >
                      <i className="ri-coin-fill text-xs" />
                      {TOKEN_REWARD} 토큰 지급
                    </span>
                  </>
                )}
              </button>
            ) : (
              <div
                className="w-full py-3.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2.5"
                style={{ background: `${accentColor}20`, border: `1px solid ${accentColor}40`, color: accentColor }}
              >
                <i className="ri-check-double-line text-base" />
                Asset 적용 완료 · {selectedVideo.user.name}에게 {TOKEN_REWARD} 토큰 지급됨
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
